import React from 'react';
import { motion } from 'framer-motion';
import { Send, BookOpen, Globe, Heart, Feather, Users } from 'lucide-react';
import { Language } from '../App';
import { PageHero } from '../components/PageHero';
interface AboutProps {
  language: Language;
}
export function About({
  language
}: AboutProps) {
  const isPashto = language === 'pashto';
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true,
      margin: '-80px' as const
    },
    transition: {
      duration: 0.6
    }
  };
  const missionPoints = isPashto ? [{
    icon: BookOpen,
    title: 'د پښتو ژبې ساتنه',
    desc: 'د پښتو ادب او ژبې ته وده ورکول، او د هغو پښتنو سره مرسته کول چې خبرې کولای شي خو پښتو لیکل یا لوستل نه شي کولای.'
  }, {
    icon: Feather,
    title: 'د لیکوالانو ملاتړ',
    desc: 'د لیکوالانو، شاعرانو او اکاډمیکانو لپاره د خپرولو پلیټفارم چمتو کول.'
  }, {
    icon: Globe,
    title: 'نړیوال رسیدنه',
    desc: 'د نړۍ په کچه پښتو معرفي کول او د نړۍ هر ګوټ کې پښتنو ته د پښتو ادب رسول.'
  }, {
    icon: Users,
    title: 'د ټولنې جوړول',
    desc: 'د لوستونکو او لیکوالانو ترمنځ اړیکه ټینګول چې ګډ هدف لري.'
  }] : [{
    icon: BookOpen,
    title: 'Preserve Pashto',
    desc: 'Promoting Pashto literature and language, and helping those Pushtuns who can speak but cannot write or read Pushto.'
  }, {
    icon: Feather,
    title: 'Support Writers',
    desc: 'Providing a publishing platform for writers, poets, and academics.'
  }, {
    icon: Globe,
    title: 'Global Reach',
    desc: 'Introducing Pushto throughout the world and bringing Pashto literature to readers everywhere.'
  }, {
    icon: Users,
    title: 'Build Community',
    desc: 'Connecting readers and writers who share a common goal.'
  }];
  return <div className="pt-24 md:pt-28" data-id="element-311">
      <PageHero language={language} titleEn="About Pasoon" titlePs="د پاڅون په اړه" introEn="A New Hope for Mother Language Pashto" introPs="د مورنۍ ژبې پښتو لپاره نوې هیله" data-id="element-312" />

      {/* Section 2: Main About Content */}
      <section className="py-8 md:py-12 bg-white px-6" data-id="element-313">
        <div className="max-w-3xl mx-auto" data-id="element-314">
          <motion.div {...fadeIn} data-id="element-315">
            {/* Welcome Line */}
            <p className={`text-pasoon-red font-bold text-xl md:text-2xl mb-6 ${isPashto ? 'font-pashto' : 'font-serif italic'}`} data-id="element-316">
              {isPashto ? 'ښه راغلاست. په خیر راغلاست.' : 'Welcome. Pa Khair Raghlast.'}
            </p>

            <div className={`space-y-6 text-gray-700 ${isPashto ? 'font-pashto leading-loose text-base' : 'leading-relaxed text-lg'}`} data-id="element-317">
              <p data-id="element-318">
                {isPashto ? 'پاڅون یوه دوه میاشتنۍ پښتو مجله ده چې د پاکستان له مردان څخه خپریږي. پاڅون مجله لومړی ځل په ۲۰۰۵ کال کې خپره شوه او له هغې وروسته یې پرته له ځنډه خپرونو ته دوام ورکړی دی. دا د پښتو یو له غوره مجلو څخه ګرځیدلې ده.' : 'Pasoon is a bi-monthly Pashto magazine published from Mardan, Pakistan. Pasoon magazine was first published in 2005 and since then has continued publishing without interruption. It has become one of the best Pushto Magazines.'}
              </p>
              <p data-id="element-319">
                {isPashto ? 'موږ هیله لرو چې دا به د وخت په تیریدو سره نوره هم ښه شي او ډیر لوړ معیار به وساتي. هغه څوک چې خپله ژبه "پښتو" خوښوي او غواړي وده ورکړي باید د پاڅون مجلې ملاتړ وکړي.' : 'We hope it will improve more with time and will maintain a very high standard. Those who love and want to promote their language "Pushto" shall support Pasoon Magazine.'}
              </p>
              <p data-id="element-320">
                {isPashto ? 'دا مجله لیکوال، شاعران، اکاډمیکان او لوستونکي سره یوځای کوي چې ګډ هدف لري — د راتلونکو نسلونو لپاره د پښتو ژبې ساتنه او وده.' : 'The magazine brings together writers, poets, academics, and readers who share a common goal. To preserve and grow the Pashto language for future generations.'}
              </p>
              <p data-id="element-321">
                {isPashto ? 'پاڅون لیکوالان او لوستونکي دواړه هڅوي چې برخه واخلي. دا د اصلي مقالو، شاعرۍ او علمي کار لپاره پلیټفارم چمتو کوي. هره ګڼه د پښتو کلتور غږ، تاریخ او فکري ژوروالی منعکسوي.' : 'Pasoon encourages both writers and readers to take part. It provides a platform for original articles, poetry, and academic work. Each edition reflects the voice, history, and intellectual depth of Pashto culture.'}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 3: History & Journey */}
      <section className="py-8 md:py-12 bg-gray-50 px-6" data-id="element-322">
        <div className="max-w-5xl mx-auto" data-id="element-323">
          <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-324">
            {/* Content or images could be added here as per design */}
          </div>
        </div>
      </section>

      {/* Section 4: Our Mission Pillars */}
      <section className="py-8 md:py-12 bg-white px-6" data-id="element-325">
        <div className="max-w-5xl mx-auto" data-id="element-326">
          <motion.div {...fadeIn} className="text-center mb-12" data-id="element-327">
            <h2 className={`text-3xl md:text-4xl font-bold text-pasoon-dark mb-4 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-328">
              {isPashto ? 'زموږ ماموریت' : 'Our Mission'}
            </h2>
            <div className="w-20 h-1 bg-pasoon-red mx-auto rounded-full" data-id="element-329"></div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-330">
            {missionPoints.map((point, idx) => <motion.div key={idx} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: idx * 0.1
          }} className="bg-white rounded-2xl p-8 border border-gray-100 shadow-sm text-start" data-id="element-331">
                <div className="w-12 h-12 rounded-full bg-pasoon-red/10 flex items-center justify-center mb-5" data-id="element-332">
                  <point.icon className="w-5 h-5 text-pasoon-red" data-id="element-333" />
                </div>
                <h3 className={`text-lg font-bold text-pasoon-dark mb-2 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-334">
                  {point.title}
                </h3>
                <p className={`text-gray-600 text-sm ${isPashto ? 'font-pashto leading-loose' : 'leading-relaxed'}`} data-id="element-335">
                  {point.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Section 5: Call to Action */}
      <section className="py-8 md:py-12 bg-pasoon-dark px-6 text-center" data-id="element-336">
        <div className="max-w-3xl mx-auto" data-id="element-337">
          <motion.div {...fadeIn} data-id="element-338">
            <h2 className={`text-3xl md:text-4xl font-bold text-pasoon-light mb-4 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-339">
              {isPashto ? 'د پاڅون سره مرسته وکړئ' : 'Support Pasoon'}
            </h2>

            <p className={`text-gray-300 max-w-xl mx-auto mb-4 ${isPashto ? 'font-pashto leading-loose' : 'text-lg leading-relaxed'}`} data-id="element-340">
              {isPashto ? 'د پاڅون سره مرسته وکړئ او د پاڅون برخه شئ. موږ ستاسو نظرونو ته هم ارزښت ورکوو — ستاسو نظرونه مجله ښه کولو او پلیټفارم پیاوړي کولو کې مرسته کوي.' : 'Support Pasoon and be part of the Pasoon community. We value your feedback. Your input helps improve the magazine and strengthen the platform.'}
            </p>

            <p className={`text-gray-400 text-sm mb-10 ${isPashto ? 'font-pashto' : ''}`} data-id="element-341">
              {isPashto ? 'هغه څوک چې د پښتو ژبې او کلتور ته ارزښت ورکوي هڅول کیږي چې د پاڅون ملاتړ وکړي.' : 'Those who value Pashto language and culture are encouraged to support Pasoon.'}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-342">
              <button className={`px-8 py-4 bg-pasoon-red text-white rounded-full font-medium text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-343">
                <span className="flex items-center gap-2" data-id="element-344">
                  <Send className="w-5 h-5" data-id="element-345" />
                  {isPashto ? 'مقاله راولیږئ' : 'Submit Article'}
                </span>
              </button>
              <button className={`px-8 py-4 bg-white border-2 border-pasoon-red text-pasoon-red rounded-full font-medium text-lg hover:bg-red-50 transition-all active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-346">
                <span className="flex items-center gap-2" data-id="element-347">
                  <Heart className="w-5 h-5" data-id="element-348" />
                  {isPashto ? 'پاڅون سره مرسته وکړئ' : 'Support Pasoon'}
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 6: Pashto Language Section */}
      <section className="py-12 md:py-16 bg-white px-6" data-id="element-349">
        <div className="max-w-3xl mx-auto" data-id="element-350">
          <motion.div {...fadeIn} data-id="element-351">
            <h2 className={`text-3xl md:text-4xl font-bold text-pasoon-dark mb-4 inline-block relative ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-352">
              {isPashto ? 'د پښتو ژبې په اړه' : 'About the Pashto Language'}
              <span className="absolute -bottom-3 start-0 w-1/3 h-1 bg-pasoon-red rounded-full" data-id="element-353"></span>
            </h2>

            <div className={`space-y-6 mt-10 text-gray-700 ${isPashto ? 'font-pashto leading-loose text-base' : 'leading-relaxed text-lg'}`} data-id="element-354">
              <p data-id="element-355">
                {isPashto ? 'پښتو یا پختو یوه ډیره لرغونې ژبه ده. دا د پاکستان په خیبر پښتونخوا او بلوچستان ایالتونو کې ویل کیږي. دا د افغانستان له دوو رسمي ژبو څخه یوه ده، چې دویمه یې دري ده. هغه خلک چې پښتو وایي د پښتنو/پختنو یا افغانانو په نوم یادیږي.' : 'Pashto or Pushto is a very ancient language. It is spoken in Khyber Pukhtunkhwa and Baluchistan Provinces of Pakistan. It is one of the two official languages of Afghanistan, the second being Dari. The people who speak Pushto are called Pushtuns/Pukhtuns or Afghans.'}
              </p>
              <p data-id="element-356">
                {isPashto ? 'پښتو په ۱۹۳۶ کال کې د افغانستان ملي ژبه اعلان شوه. په پاکستان کې دا په ولایتي کچه رسماً نه ده پیژندل شوې، خو دا په کلتور او هویت کې ژورې ریښې لري.' : 'Pashto was declared the national language of Afghanistan in 1936. In Pakistan, it is not officially recognised at provincial level, but it remains deeply rooted in culture and identity.'}
              </p>

              {/* Highlighted stat */}
              <div className="relative my-10 py-8 px-6 md:px-10 bg-pasoon-green/30 rounded-2xl" data-id="element-357">
                <p className={`text-xl md:text-2xl font-bold text-pasoon-dark ${isPashto ? 'font-pashto leading-normal' : 'font-serif'}`} data-id="element-358">
                  {isPashto ? 'پښتو د نړۍ په کچه د ملیونونو خلکو لخوا ویل کیږي. سره له دې، ډیری ویونکي یې لوستل او لیکل نه شي کولای.' : 'Pashto is spoken by millions of people worldwide. Despite this, many speakers cannot read or write the language.'}
                </p>
              </div>

              <p data-id="element-359">
                {isPashto ? 'پاڅون هدف لري چې دا تشه ډکه کړي. دا سواد ته وده ورکوي، زده کړه هڅوي، او د ډیجیټل عصر کې د پښتو ودې ته ملاتړ کوي.' : 'Pasoon aims to bridge this gap. It promotes literacy, encourages learning, and supports the growth of Pashto in the digital age.'}
              </p>
              <p data-id="element-360">
                {isPashto ? 'د پوهاوي او ټیکنالوژۍ ته د لاسرسي په زیاتیدو سره، تمه کیږي چې پښتو به په راتلونکو کلونو کې نوره هم پراخه شي.' : 'With increasing awareness and access to technology, Pashto is expected to expand further in the coming years.'}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 7: Contribution Call */}
      <section className="py-8 md:py-12 bg-gradient-to-b from-pasoon-green/20 to-white px-6" data-id="element-361">
        <div className="max-w-3xl mx-auto text-center" data-id="element-362">
          <motion.div {...fadeIn} data-id="element-363">
            <div className="w-16 h-16 rounded-full bg-pasoon-red/10 flex items-center justify-center mx-auto mb-6" data-id="element-364">
              <Send className="w-7 h-7 text-pasoon-red" data-id="element-365" />
            </div>

            <h2 className={`text-3xl md:text-4xl font-bold text-pasoon-dark mb-4 ${isPashto ? 'font-pashto' : 'font-serif'}`} data-id="element-366">
              {isPashto ? 'خپل آثار راولیږئ' : 'Submit Your Work'}
            </h2>

            <p className={`text-gray-600 max-w-xl mx-auto mb-4 ${isPashto ? 'font-pashto leading-loose' : 'text-lg leading-relaxed'}`} data-id="element-367">
              {isPashto ? 'خپل آثار راولیږئ او د پاڅون برخه شئ. موږ ستاسو نظرونو ته هم ارزښت ورکوو — ستاسو نظرونه مجله ښه کولو او پلیټفارم پیاوړي کولو کې مرسته کوي.' : 'Submit your work and be part of Pasoon. We also value feedback. Your input helps improve the magazine and strengthen the platform.'}
            </p>

            <p className={`text-gray-500 text-sm mb-10 ${isPashto ? 'font-pashto' : ''}`} data-id="element-368">
              {isPashto ? 'هغه څوک چې د پښتو ژبې او کلتور ته ارزښت ورکوي هڅول کیږي چې د پاڅون ملاتړ وکړي.' : 'Those who value Pashto language and culture are encouraged to support Pasoon.'}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-369">
              <button className={`px-8 py-4 bg-pasoon-red text-white rounded-full font-medium text-lg hover:bg-red-800 transition-all shadow-lg shadow-red-900/20 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-370">
                <span className="flex items-center gap-2" data-id="element-371">
                  <Send className="w-5 h-5" data-id="element-372" />
                  {isPashto ? 'مقاله راولیږئ' : 'Submit Article'}
                </span>
              </button>
              <button className={`px-8 py-4 bg-white border-2 border-pasoon-red text-pasoon-red rounded-full font-medium text-lg hover:bg-red-50 transition-all active:scale-[0.98] ${isPashto ? 'font-pashto' : ''}`} data-id="element-373">
                <span className="flex items-center gap-2" data-id="element-374">
                  <Heart className="w-5 h-5" data-id="element-375" />
                  {isPashto ? 'پاڅون سره مرسته وکړئ' : 'Support Pasoon'}
                </span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}